import type { PlanDetailsCopy } from '../planDetails'

const id: PlanDetailsCopy = {
  fieldLabels: {
    current_age: 'Usia Anda',
    retirement_age: 'Kapan Anda pensiun',
    annual_income: 'Pendapatan tahunan',
    annual_savings: 'Tabungan tahunan',
    inflation: 'Laju inflasi',
    realization_tax_rate: 'Tarif pajak keuntungan modal',
    cost_basis: 'Harga perolehan',
    w0: 'Aset yang dapat diinvestasikan saat ini',
    primary_residence: 'Rumah Anda',
    total_investable: 'Aset yang dapat diinvestasikan',
    expected_return: 'Imbal hasil yang diharapkan',
    volatility: 'Volatilitas',
    horizon: 'Horizon perencanaan',
    jurisdiction: 'Domisili pajak',
    risk_aversion: 'Toleransi risiko',
    birth_year: 'Tahun lahir',
    birth_date: 'Tanggal lahir',
    marital_status: 'Status pernikahan',
    dependents: 'Tanggungan',
    dependent_ages: 'Usia tanggungan',
    children: 'Anak',
    children_birth_years: 'Tahun lahir anak',
    partner_age: 'Usia pasangan',
    partner_annual_income: 'Pendapatan tahunan pasangan',
    partner_retirement_age: 'Usia pensiun pasangan',
    plan_to_age: 'Usia akhir rencana',
    gross_annual_income: 'Pendapatan bruto tahunan',
    net_income: 'Pendapatan bersih',
    annual_spending: 'Pengeluaran tahunan',
    essential_spending: 'Pengeluaran pokok',
    flexible_spending: 'Pengeluaran fleksibel',
    investments_total: 'Total investasi',
    total_investable_wealth: 'Total aset yang dapat diinvestasikan',
    portfolio_mix: 'Komposisi portofolio',
    cgt_rate: 'Tarif pajak atas hasil modal',
    interest_rate: 'Suku bunga',
    dividend_yield: 'Imbal hasil dividen',
    wealth_tax_rate: 'Tarif pajak kekayaan',
    filing_jurisdictions: 'Negara pelaporan pajak',
    citizenship: 'Kewarganegaraan',
    tax_residency: 'Domisili pajak yang dinyatakan',
    state: 'Negara bagian atau provinsi',
    region: 'Wilayah',
    city: 'Kota',
    locality: 'Kotamadya',
    church_tax_applicable: 'Dikenai pajak gereja',
    life_cover: 'Pertanggungan asuransi jiwa',
    income_protection: 'Asuransi perlindungan pendapatan',
    employer_match: 'Kontribusi pemberi kerja',
    pension: 'Dana pensiun',
    state_pension: 'Pensiun negara',
    social_security: 'Jaminan sosial',
    wage_growth: 'Pertumbuhan gaji',
    stability: 'Stabilitas pendapatan',
    has_will: 'Memiliki surat wasiat',
    risk_attitude: 'Sikap terhadap risiko',
  },
  keyLabels: {
    goal_priority: 'Urutan tujuan',
    current_age: 'Usia',
    inflation: 'Asumsi inflasi',
    jurisdiction: 'Domisili pajak',
    realization_tax_rate: 'Pajak saat menjual untuk mendanai tujuan',
    citizenship: 'Kewarganegaraan',
    total_investable: 'Kekayaan yang dapat diinvestasikan',
    annual_income: 'Pendapatan tahunan',
    annual_savings: 'Tabungan tahunan',
    risk_tier: 'Tingkat risiko',
    retirement_age: 'Usia pensiun',
    target_wealth: 'Target kekayaan',
    primary_residence_sellable: 'Menjual rumah / pindah ke rumah lebih kecil',
  },
  channelLabels: {
    web_levers: 'Pengaturan di situs',
    web_cockpit: 'Panel keputusan',
    web: 'Situs web',
    chat: 'Asisten AI',
    mcp: 'Asisten AI',
    full_review: 'Tinjauan lengkap',
    full_plan: 'Rencana lengkap',
    what_if: 'Analisis skenario',
  },
  objectiveLabels: {
    status_quo: 'Kondisi saat ini',
    max_goal_probability: 'Probabilitas keberhasilan tertinggi',
    lowest_drawdown: 'Penurunan maksimum terendah',
    highest_growth: 'Pertumbuhan tertinggi',
    fastest_to_goal: 'Tujuan tercapai paling cepat',
  },
  choices: {
    interest_only: 'Hanya bunga',
    fixed_rate_amortizing: 'Cicilan pokok dan bunga (bunga tetap)',
    variable_rate_amortizing: 'Cicilan pokok dan bunga (bunga mengambang)',
    repayment: 'Cicilan pokok dan bunga',
    refinance: 'Refinansiasi dengan suku bunga pasar',
    repay: 'Lunasi sepenuhnya',
  },
  values: {
    yearsShort: (n) => `${n} thn`,
    shareAbout: (pct) => `sekitar ${pct} dari nilainya`,
    sharePart: 'sebagian nilainya',
    aroundAge: (age) => ` sekitar usia ${age}`,
    inAboutYears: (n) => ` dalam sekitar ${n} tahun`,
    maySellFor: (share, when) => `Dapat dijual untuk melepaskan ${share}${when}, hanya jika hal itu memperbaiki rencana.`,
    maySell: 'Dapat dijual jika hal itu memperbaiki rencana.',
    structuredFact: 'Data terstruktur',
  },
  ask: { talkThrough: 'Bicarakan dengan MyCIO' },
  review: {
    openInputCheckFailed: 'Pemeriksaan data yang belum lengkap tidak dapat diselesaikan. Lihat status tinjauan di bawah.',
    finishedLoading: 'Tinjauan Anda sudah selesai. Memuat hasilnya…',
    draftReady: 'Tinjauan sementara Anda sudah siap — di bawah ini ada analisis yang sudah selesai, bagian yang belum dapat dihitung, dan pertanyaan yang dapat melengkapinya.',
    finishedNotLoaded: 'Tinjauan Anda sudah selesai, tetapi hasilnya belum dapat dimuat. Mencoba lagi — perhitungan tidak akan dijalankan ulang.',
    fetchFailed: 'Tinjauan Anda sudah selesai, tetapi saat ini belum dapat diambil. Hasilnya tersimpan — coba lagi sebentar lagi; tidak perlu dihitung ulang.',
    notComputed: (list) => `Tidak dihitung pada proses ini: ${list}.`,
    changesNotSaved: (_n, f) => `${f} perubahan tidak tersimpan — lihat Informasi Anda.`,
  },
  facts: {
    chooseOption: 'Pilih salah satu opsi.',
    couldNotSave: 'Nilai itu tidak dapat disimpan. Rencana Anda tidak berubah.',
    couldNotUndo: 'Perubahan itu tidak dapat dibatalkan.',
    choosePlaceholder: 'Pilih…',
    confirmedBadge: 'Sudah dikonfirmasi — minta perubahan lewat chat',
    confirmedTitle: (was) => `Anda sebelumnya mengonfirmasi data ini sebagai ${was}. Minta MyCIO lewat chat untuk menggantinya.`,
  },
  assumptionsToConfirm: { anInput: 'suatu masukan', assumedSuffix: (v) => ` — diasumsikan: ${v}` },
  editor: {
    enterNumber: 'Masukkan angka.',
    enterNumberFor: (l) => `Masukkan angka untuk ${l}.`,
    enterPercentFor: (l) => `Masukkan persentase untuk ${l}.`,
    chooseYesNoFor: (l) => `Pilih Ya atau Tidak untuk ${l}.`,
    semicolonBetween: (l) => `Pisahkan item ${l} dengan ";".`,
    useNumbersFor: (l) => `Gunakan angka untuk ${l}.`,
    wholeNumbersFor: (l) => `Gunakan bilangan bulat untuk ${l}, dipisahkan dengan ";".`,
    commaSeparated: 'dipisahkan koma',
  },
  freshnessUnknown: 'Kami tidak dapat memastikan apakah perhitungan tersimpan Anda sudah mencakup perubahan rekening terbaru. Muat ulang halaman dan periksa rekening Anda sebelum mengandalkan angka-angka ini.',
  goals: { goalN: (n) => `Tujuan ${n}`, whatMoves: 'yang memengaruhinya →', earliest: (y) => `paling cepat ≈ ${y}`, rank: 'Peringkat' },
  issues: {
    mainConstraint: 'Kendala utama rencana ini',
    riskToReview: 'Risiko untuk ditinjau',
    missingInformation: 'Informasi yang belum ada',
    whyItMatters: (t) => `Mengapa penting: ${t}`,
    seeEvidence: 'Lihat bukti pendukung ↓',
    noneFound: 'Analisis yang telah selesai tidak menemukan masalah material dari informasi yang saat ini ada di rencana ini.',
    notAvailable: 'Analisis masalah belum tersedia. Bagian yang kosong ini tidak berarti tidak ada masalah.',
    discussQuestion: 'Tinjau masalah yang ditemukan rencana ini. Jelaskan buktinya dan tanyakan apakah ada bagian dari keadaan saya yang belum diketahui yang dapat mengubah prioritas atau kesimpulannya.',
    discussLabel: 'Diskusikan masalah ini',
  },
  policies: {
    optionTested: 'Opsi yang diuji',
    computedEffect: (v) => `Dampak yang dihitung: ${v}`,
    tradeoff: (v) => `Kompromi: ${v}`,
    backToAction: 'Kembali ke tindakan ↑',
    tradeoffsHeading: 'Kompromi dari kasus yang diuji',
  },
  transfer: {
    toHeirs: (m) => `+${m} untuk ahli waris`,
    fundedPoints: (label, pts) => `${label} ${pts} poin persentase`,
    noChange: ' · tidak mengubah pendanaan tujuan Anda',
  },
  tax: {
    rulesIntro: 'Hanya aturan yang terlampir pada perhitungan ini yang ditampilkan. Setiap entri menunjukkan detail sumber yang tercatat; tanggal atau informasi cakupan yang tidak ada tetap ditandai sebagai tidak ada.',
    title: 'Perlakuan pajak yang digunakan dalam rencana ini',
    countryBookPrimary: 'Proyeksi menerapkan ketentuan pajak negara terkait dari tahun ke tahun. Sumber yang secara tegas dicatat sebagai resmi atau primer tercantum di bawah.',
    countryBookNoPrimary: 'Proyeksi menerapkan ketentuan pajak negara terkait dari tahun ke tahun. Tautan ke sumber primer belum dilampirkan pada hasil tersimpan ini, jadi periksa keputusan yang terkait pajak sebelum bertindak.',
    simplified: 'Perhitungan ini menggunakan perlakuan pajak yang disederhanakan. Tinjau batasan di bawah sebelum mengandalkan perbandingan yang terkait pajak.',
    askQuestion: 'Jelaskan undang-undang pajak dan lapisan negara mana yang diterapkan pada rencana ini, mana yang didekati atau dikecualikan, dan fakta apa tentang posisi pajak saya yang dapat mengubah hasilnya. Gunakan hanya sumber primer yang benar-benar terlampir pada perhitungan ini dan sebutkan sumber yang belum ada.',
    askLabel: 'Tanyakan tentang perlakuan pajak',
  },
  inputs: { title: 'Masukan yang dapat mengubah hasil', infoToConfirm: 'Informasi untuk dikonfirmasi' },
  gated: {
    countChoices: (_n, f) => `${f} pilihan yang kami hitung untuk Anda`,
    mappedYears: (_n, f) => `kami memetakan ${f} tahun ke depan`,
    extraChecks: (_n, f) => `${f} cara tambahan kami memeriksa rencana Anda`,
    placeholderRows: ['Baris ilustrasi', 'Angka contoh', 'Opsi contoh', 'Tempat penampung'],
  },
}

export default id
